import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './BlogPost.scss';

// dynamically pull in every post module
const req = require.context(
  '../BlogPostsContent',
  false,
  /\.jsx?$/
);

const posts = req
  .keys()
  .map((key) => req(key).default)
  .filter((post) => post && post.id);

const orderedPosts = [...posts].sort((a, b) => new Date(b.date) - new Date(a.date));

const BlogPost = () => {
  const { blogId } = useParams();
  const blog = orderedPosts.find((post) => post.id.toString() === blogId);

  if (!blog) return <div>Blog post not found.</div>;

  const currentIndex = orderedPosts.findIndex((post) => post.id.toString() === blogId);
  const previousPost = orderedPosts[currentIndex + 1];
  const nextPost = orderedPosts[currentIndex - 1];
  const canonicalUrl = `https://www.kleanleaf.us/blogs/${blog.id}/`;
  const encodedUrl = encodeURIComponent(canonicalUrl);
  const encodedTitle = encodeURIComponent(blog.title);
  const shareLinks = [
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: <FaFacebookF />,
      className: 'facebook',
    },
    {
      name: 'X',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: <FaXTwitter />,
      className: 'x',
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/',
      icon: <FaInstagram />,
      className: 'instagram',
      onClick: () => {
        if (navigator.clipboard) {
          navigator.clipboard.writeText(canonicalUrl);
        }
      },
    },
    {
      name: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: <FaLinkedinIn />,
      className: 'linkedin',
    },
  ];
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: blog.title,
    description: blog.metaTags.description,
    datePublished: blog.date,
    author: {
      "@type": "Organization",
      name: blog.metaTags.author || "Klean Leaf",
    },
    publisher: {
      "@type": "Organization",
      name: "Klean Leaf",
    },
    mainEntityOfPage: canonicalUrl,
  };

  return (
    <article className="blog-post-container">
      <Helmet>
        <title>{blog.title}</title>
        <meta name="description" content={blog.metaTags.description} />
        <meta name="keywords" content={blog.metaTags.keywords} />
        <meta name="author" content={blog.metaTags.author} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.metaTags.description} />
        <meta property="og:image" content={blog.imageUrl1} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.metaTags.description} />
        <meta name="twitter:image" content={blog.imageUrl1} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <header className="blog-post-header">
        <h1 className="blog-post-title">{blog.title}</h1>
        <p className="blog-post-date">{blog.date}</p>
        <nav className="blog-share-links" aria-label="Share this article">
          {shareLinks.map((link) => (
            <a
              key={link.name}
              className={`blog-share-link ${link.className}`}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={link.onClick}
              aria-label={`Share on ${link.name}`}
              title={link.name === 'Instagram' ? 'Copy link and open Instagram' : `Share on ${link.name}`}
            >
              {link.icon}
              <span>{link.name}</span>
            </a>
          ))}
        </nav>
      </header>

      <section className="blog-post-content">
        <img
          src={blog.imageUrl1}
          alt={blog.title}
          className="blog-post-image"
        />
        <div
          className="blog-text-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />
      </section>

      <nav className="blog-post-navigation" aria-label="Blog post navigation">
        {previousPost ? (
          <Link to={`/blogs/${previousPost.id}`} className="blog-nav-link blog-nav-previous">
            <FiChevronLeft className="blog-nav-icon" />
            <span className="blog-nav-kicker">Previous</span>
            <span className="blog-nav-title">{previousPost.title}</span>
          </Link>
        ) : (
          <span />
        )}

        {nextPost ? (
          <Link to={`/blogs/${nextPost.id}`} className="blog-nav-link blog-nav-next">
            <span className="blog-nav-kicker">Next</span>
            <span className="blog-nav-title">{nextPost.title}</span>
            <FiChevronRight className="blog-nav-icon" />
          </Link>
        ) : (
          <span />
        )}
      </nav>
    </article>
  );
};

export default BlogPost;
